import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for creating a typing animation effect
 * 
 * @param {string[]} texts - Array of strings to cycle through
 * @param {number} typingSpeed - Speed for typing animation (ms per character)
 * @param {number} deletingSpeed - Speed for deleting animation (ms per character)
 * @param {number} delayAfterType - Delay after typing completes (ms)
 * @param {number} delayAfterDelete - Delay after deleting completes (ms)
 * @param {boolean} externalPaused - External control to pause the animation (e.g. when user focuses input)
 * @returns {string} - Current text being animated
 */
const useTypingEffect = (
  texts = [], 
  typingSpeed = 80, 
  deletingSpeed = 50, 
  delayAfterType = 2000, 
  delayAfterDelete = 500,
  externalPaused = false
) => {
  const [textIndex, setTextIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [internalPaused, setInternalPaused] = useState(false);

  // Function to get the current full text
  const getCurrentText = useCallback(() => {
    return texts[textIndex % texts.length] || '';
  }, [textIndex, texts]);

  // Effect for handling the typing/deleting animation
  useEffect(() => {
    // Skip if no texts are provided or if externally paused
    if (texts.length === 0 || externalPaused) return;
    
    let timeout;
    const currentFullText = getCurrentText();

    if (isTyping) {
      // Typing animation
      if (displayText.length < currentFullText.length) {
        timeout = setTimeout(() => {
          setDisplayText(currentFullText.slice(0, displayText.length + 1));
        }, typingSpeed);
      } else {
        // Finished typing, pause before deleting
        timeout = setTimeout(() => {
          setInternalPaused(false);
          setIsTyping(false);
        }, delayAfterType);
        setInternalPaused(true);
      }
    } else {
      // Deleting animation
      if (displayText.length > 0) {
        timeout = setTimeout(() => {
          setDisplayText(displayText.slice(0, -1));
        }, deletingSpeed);
      } else {
        // Finished deleting, move to next text and start typing again
        timeout = setTimeout(() => {
          setIsTyping(true);
          setTextIndex(prevIndex => prevIndex + 1);
        }, delayAfterDelete);
      }
    }

    // Cleanup function
    return () => clearTimeout(timeout);
  }, [
    displayText, 
    isTyping, 
    internalPaused, 
    textIndex, 
    texts, 
    typingSpeed, 
    deletingSpeed, 
    delayAfterType,
    delayAfterDelete,
    getCurrentText,
    externalPaused
  ]);

  return displayText;
};

export default useTypingEffect;
